import { api } from "./client";

const base = (slug: string) => `/v1/c/${slug}`;

export interface ContactDetail {
  contact: {
    id: string;
    displayName: string;
    firstName: string | null;
    lastName: string | null;
    address: string | null;
    channelKind: string | null;
    source: string;
    externalRef: string | null;
    locale: string | null;
    timezone: string | null;
    fields: Record<string, unknown>;
    isBlocked: boolean;
    blockedReason: string | null;
    lastActivityAt: string | null;
    createdAt: string;
  };
  identities: Array<{
    id: string;
    channel_kind: string;
    address: string | null;
    is_primary: boolean;
    last_seen_at: string | null;
  }>;
  consents: Array<{
    channel_kind: string;
    purpose: string;
    status: string;
    source: string;
    occurred_at: string;
  }>;
  conversations: Array<{
    /** "history": chats copied from the WhatsApp Business app when the number was connected, or imported. */
    source_kind?: string;
    /** For history brought by an import (decision 0024): the platform it came from. */
    imported_from?: string | null;
    id: string;
    status: string;
    created_at: string;
    closed_at: string | null;
    outcome_code: string | null;
    is_success: boolean | null;
  }>;
  tags: Array<{
    id: string;
    /** Per locale, like every tag name. */
    name: Record<string, string> | string;
    color: string | null;
  }>;
  fieldDefinitions: Array<{
    key: string;
    label: Record<string, string> | string;
    type: string;
    options: unknown;
    ai_extractable: boolean;
    required: boolean;
    sort: number;
  }>;
}

export interface ContactPatch {
  displayName?: string;
  firstName?: string | null;
  lastName?: string | null;
  locale?: "pt-BR" | "es" | "en" | null;
  externalRef?: string | null;
  fields?: Record<string, unknown>;
  isBlocked?: boolean;
  blockedReason?: string | null;
}

export interface ContactMediaItem {
  id: string;
  conversationId: string;
  direction: string;
  kind: "image" | "video" | "audio" | "sticker" | "document" | "link";
  group: "media" | "docs" | "links";
  url: string;
  mime?: string | null;
  filename?: string | null;
  sizeBytes?: number | null;
  caption?: string | null;
  text?: string;
  createdAt: string;
}
/** A row of the contacts list. */
export interface ContactRow {
  id: string;
  displayName: string;
  address: string | null;
  lastActivityAt: string | null;
  tags: Array<{
    id: string;
    name: Record<string, string> | string;
    color: string | null;
  }>;
}
export const contactsApi = {
  list: (slug: string, query: { q?: string; tag?: string; limit?: number }) =>
    api<{ contacts: ContactRow[] }>(`${base(slug)}/contacts`, {
      query: {
        q: query.q?.trim() || undefined,
        tag: query.tag,
        limit: query.limit ?? 50,
      },
    }),
  media: (slug: string, id: string, kind?: "media" | "docs" | "links") =>
    api<{
      counts: { media: number; docs: number; links: number };
      items: ContactMediaItem[];
    }>(`${base(slug)}/contacts/${id}/media`, { query: kind ? { kind } : {} }),
  get: (slug: string, id: string) =>
    api<ContactDetail>(`${base(slug)}/contacts/${id}`),
  patch: (slug: string, id: string, body: ContactPatch) =>
    api<{ ok: true }>(`${base(slug)}/contacts/${id}`, {
      method: "PATCH",
      body,
    }),
  consent: (
    slug: string,
    id: string,
    body: {
      purpose: "service" | "utility" | "marketing";
      status: "opted_in" | "opted_out";
    },
  ) =>
    api(`${base(slug)}/contacts/${id}/consents`, {
      method: "POST",
      body: { ...body, source: "form" },
    }),
};
